import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, X, Search, Filter, Crown } from 'lucide-react';
import { WorkoutCategory } from './library/WorkoutCategory';
import { WorkoutDetail } from './detail/WorkoutDetail';
import { WorkoutPlayer2 } from './player/WorkoutPlayer2';
import { WorkoutRecommendations } from './WorkoutRecommendations';
import { WorkoutFilters } from './library/WorkoutFilters';
import { WorkoutSearch } from './library/WorkoutSearch';
import { WorkoutScheduler } from './scheduling/WorkoutScheduler';
import { WorkoutBuilder } from './create/WorkoutBuilder';
import { workoutCategories, workouts } from '../../data/workouts';
import { useHealthMetrics } from '../../hooks/useHealthMetrics';
import { ProgramsTab } from './ProgramsTab';
import { CustomWorkout } from '../../types/exercises';

interface WorkoutsLibraryProps {
  onClose: () => void;
}

export function WorkoutsLibrary({ onClose }: WorkoutsLibraryProps) {
  const [activeTab, setActiveTab] = useState<'workouts' | 'programs'>('workouts');
  const [selectedWorkout, setSelectedWorkout] = useState<string | null>(null);
  const [isWorkoutActive, setIsWorkoutActive] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [selectedDuration, setSelectedDuration] = useState<number | null>(null);
  const [selectedStrain, setSelectedStrain] = useState('All');
  const metrics = useHealthMetrics();

  const handleWorkoutSelect = (workoutId: string) => {
    const workout = workouts.find(w => w.id === workoutId);
    if (!workout) return;

    setSelectedWorkout(workoutId);
  };

  const handleStartWorkout = (workout: any) => {
    setIsWorkoutActive(true);
  };

  const handleScheduleWorkout = () => {
    setShowScheduler(true);
  };

  const handleSaveCustomWorkout = (workout: CustomWorkout) => {
    console.log('Custom workout saved:', workout);
    setShowBuilder(false);
  };

  const filteredWorkouts = workouts.filter(workout => {
    const matchesSearch = workout.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLevel = selectedLevel === 'All' || workout.level === selectedLevel;
    const matchesDuration = !selectedDuration || workout.duration === selectedDuration;
    return matchesSearch && matchesLevel && matchesDuration;
  });

  const selectedWorkoutData = workouts.find(w => w.id === selectedWorkout);

  if (showBuilder) {
    return (
      <WorkoutBuilder
        onClose={() => setShowBuilder(false)}
        onSave={handleSaveCustomWorkout}
      />
    );
  }

  if (isWorkoutActive && selectedWorkoutData) {
    return (
      <WorkoutPlayer2
        workout={{
          id: selectedWorkoutData.id,
          name: selectedWorkoutData.name,
          exercises: selectedWorkoutData.exercises,
          targetStrain: selectedWorkoutData.targetStrain
        }}
        onClose={() => setIsWorkoutActive(false)}
      />
    );
  }

  if (showScheduler && selectedWorkoutData) {
    return (
      <WorkoutScheduler
        workout={selectedWorkoutData}
        onClose={() => setShowScheduler(false)}
        onScheduled={() => {
          setShowScheduler(false);
          setSelectedWorkout(null);
        }}
      />
    );
  }

  if (selectedWorkoutData) {
    return (
      <WorkoutDetail
        workout={selectedWorkoutData}
        metrics={metrics}
        onClose={() => setSelectedWorkout(null)}
        onStart={() => setIsWorkoutActive(true)}
        onSchedule={handleScheduleWorkout}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-background/90 backdrop-blur-sm z-30">
      <div className="h-[calc(100vh-5rem)] overflow-y-auto theme-scrollbar pt-20">
        <div className="max-w-7xl mx-auto p-6 pb-24 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <X className="w-6 h-6 text-muted-foreground hover:text-foreground" />
              </button>
              <h1 className="text-xl font-semibold">Workout Library</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${
                  showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/50'
                }`}
              >
                <Filter className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setShowBuilder(true)}
                className="theme-button px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Workout
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 border-b border-border">
            <button
              onClick={() => setActiveTab('workouts')}
              className={`px-4 py-2 border-b-2 transition-colors ${
                activeTab === 'workouts'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Single Workouts
            </button>
            <button
              onClick={() => setActiveTab('programs')}
              className={`px-4 py-2 border-b-2 transition-colors ${
                activeTab === 'programs'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Programs
            </button>
          </div>

          {activeTab === 'workouts' ? (
            <>
              {/* Search and Filters */}
              <div className="space-y-4">
                <WorkoutSearch
                  value={searchQuery}
                  onChange={setSearchQuery}
                />

                {showFilters && (
                  <WorkoutFilters
                    selectedLevel={selectedLevel}
                    onLevelChange={setSelectedLevel}
                    selectedDuration={selectedDuration}
                    onDurationChange={setSelectedDuration}
                    selectedTarget="All"
                    onTargetChange={() => {}}
                  />
                )}
              </div>

              {/* Recommendations */}
              <WorkoutRecommendations
                metrics={metrics}
                onSelectWorkout={(workout) => setSelectedWorkout(workout.type)}
              />

              {/* Categories */}
              <div className="space-y-8">
                {workoutCategories.map((category) => {
                  const categoryWorkouts = filteredWorkouts.filter(w => w.category === category.id);
                  if (categoryWorkouts.length === 0) return null;
                  
                  return (
                    <WorkoutCategory
                      key={category.id}
                      category={category}
                      workouts={categoryWorkouts}
                      onSelectWorkout={handleWorkoutSelect}
                    />
                  );
                })}
              </div>
            </>
          ) : (
            <ProgramsTab />
          )}
        </div>
      </div>
    </div>
  );
}